// /tools/trees — viewer for authored tree archetypes.
//
// Three-panel layout matching the asset viewer: archetype list on
// the left, R3F viewport in the center on the shared StageScene,
// inspector on the right. Lists archetypes via the build-time
// /archetypes/_manifest.json; renders the selected archetype's
// SegmentGraph as a chain of truncated cones via TreeMesh.

import { useEffect, useMemo, useState } from 'react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei';
import { Box3, Vector3 } from 'three';
import type { SegmentGraph } from 'heartwood-tree-gen';
import { palette, scene } from '../../theme';
import { StageScene, ViewportGrid } from '../shared/StageScene';
import { TreeMesh } from './TreeMesh';

interface ArchetypeManifest {
  archetypes: string[];
}

export function TreesViewer(): React.ReactElement {
  const [manifest, setManifest] = useState<ArchetypeManifest | null>(null);
  const [manifestError, setManifestError] = useState<string | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [graph, setGraph] = useState<SegmentGraph | null>(null);
  const [graphError, setGraphError] = useState<string | null>(null);

  // Read selection from URL query (?archetype=<id>) so the viewUrl
  // emitted by `wood trees author` lands on the right archetype.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const fromUrl = params.get('archetype');
    if (fromUrl) setSelected(fromUrl);
  }, []);

  // Fetch the manifest once on mount.
  useEffect(() => {
    let cancelled = false;
    fetch('/archetypes/_manifest.json')
      .then(async r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return (await r.json()) as ArchetypeManifest;
      })
      .then(m => {
        if (cancelled) return;
        setManifest(m);
        // Auto-select the first archetype if no URL selection was made.
        setSelected(prev => prev ?? m.archetypes[0] ?? null);
      })
      .catch(e => {
        if (cancelled) return;
        setManifestError(`could not load /archetypes/_manifest.json: ${e.message ?? e}`);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Fetch the selected archetype whenever the selection changes.
  useEffect(() => {
    if (!selected) {
      setGraph(null);
      return;
    }
    let cancelled = false;
    setGraph(null);
    setGraphError(null);
    fetch(`/archetypes/${selected}.json`)
      .then(async r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return (await r.json()) as SegmentGraph;
      })
      .then(g => {
        if (cancelled) return;
        setGraph(g);
      })
      .catch(e => {
        if (cancelled) return;
        setGraphError(`could not load ${selected}: ${e.message ?? e}`);
      });
    return () => {
      cancelled = true;
    };
  }, [selected]);

  return (
    <div style={{ position: 'absolute', inset: 0, display: 'flex' }}>
      <ArchetypeList
        names={manifest?.archetypes ?? []}
        selected={selected}
        onSelect={setSelected}
        error={manifestError}
      />
      <Viewport graph={graph} error={graphError} />
      <Inspector graph={graph} selectedName={selected} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Left panel: archetype list
// ---------------------------------------------------------------------------

function ArchetypeList({
  names,
  selected,
  onSelect,
  error,
}: {
  names: readonly string[];
  selected: string | null;
  onSelect: (id: string) => void;
  error: string | null;
}) {
  return (
    <aside
      style={{
        width: 220,
        background: 'rgba(0,0,0,0.35)',
        borderRight: `1px solid ${palette.mossTeal}`,
        padding: '12px 12px 0',
        overflowY: 'auto',
        fontSize: 13,
      }}
    >
      <div style={{ opacity: 0.6, marginBottom: 8, letterSpacing: 1 }}>archetypes</div>
      {error && (
        <div style={{ opacity: 0.7, color: '#ff8888', fontSize: 11 }}>
          {error}
          <div style={{ opacity: 0.5, marginTop: 4 }}>
            run `pnpm wood trees author …` to create one.
          </div>
        </div>
      )}
      {!error && names.length === 0 && (
        <div style={{ opacity: 0.55, fontSize: 11 }}>
          no archetypes yet — run `pnpm wood trees author &lt;name&gt; …`.
        </div>
      )}
      <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
        {names.map(name => {
          const active = name === selected;
          return (
            <li key={name} style={{ marginBottom: 4 }}>
              <button
                type="button"
                onClick={() => onSelect(name)}
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  padding: '6px 8px',
                  border: 'none',
                  borderRadius: 4,
                  background: active ? palette.acornOrange : 'rgba(255,255,255,0.04)',
                  color: active ? palette.barkBrown : palette.warmBeige,
                  fontWeight: active ? 600 : 400,
                  cursor: 'pointer',
                  fontFamily: 'system-ui, sans-serif',
                  fontSize: 13,
                }}
              >
                {name}
              </button>
            </li>
          );
        })}
      </ul>
    </aside>
  );
}

// ---------------------------------------------------------------------------
// Center: R3F viewport on the shared StageScene
// ---------------------------------------------------------------------------

function Viewport({ graph, error }: { graph: SegmentGraph | null; error: string | null }) {
  const cameraTarget = useMemo<[number, number, number]>(() => {
    if (!graph) return [0, 4, 0];
    const box = boundingBox(graph);
    return [
      (box.min.x + box.max.x) / 2,
      (box.min.y + box.max.y) / 2,
      (box.min.z + box.max.z) / 2,
    ];
  }, [graph]);

  return (
    <div style={{ flex: 1, position: 'relative', minHeight: 0 }}>
      {error && (
        <div
          style={{
            position: 'absolute',
            inset: 16,
            zIndex: 10,
            color: '#ff8888',
            fontSize: 13,
            opacity: 0.85,
            pointerEvents: 'none',
          }}
        >
          {error}
        </div>
      )}
      <Canvas
        shadows
        camera={{ position: [12, 9, 12], fov: 45 }}
        style={{ background: scene.sky, width: '100%', height: '100%' }}
      >
        <StageScene />
        <ViewportGrid />
        {graph && <TreeMesh graph={graph} />}
        <OrbitControls makeDefault target={cameraTarget} />
      </Canvas>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Right panel: inspector
// ---------------------------------------------------------------------------

function Inspector({
  graph,
  selectedName,
}: {
  graph: SegmentGraph | null;
  selectedName: string | null;
}) {
  return (
    <aside
      style={{
        width: 280,
        background: 'rgba(0,0,0,0.35)',
        borderLeft: `1px solid ${palette.mossTeal}`,
        padding: '12px 14px',
        overflowY: 'auto',
        fontSize: 12,
        lineHeight: 1.5,
      }}
    >
      <div style={{ opacity: 0.6, marginBottom: 8, letterSpacing: 1 }}>inspector</div>
      {!graph && selectedName && (
        <div style={{ opacity: 0.55 }}>loading {selectedName}…</div>
      )}
      {!graph && !selectedName && (
        <div style={{ opacity: 0.55 }}>select an archetype to inspect.</div>
      )}
      {graph && (
        <>
          <Field label="name" value={graph.metadata.archetypeName} />
          <Field label="id" value={graph.id} />
          <Field label="strategy" value={graph.strategy} />
          <Field label="preview seed" value={String(graph.previewSeed)} />
          <Field label="generator" value={graph.metadata.generatorVersion} />
          <Field label="segments" value={String(graph.segments.length)} />
          <Field
            label="generated"
            value={graph.metadata.generatedAt.replace('T', ' ').slice(0, 19)}
          />
          {graph.metadata.description && (
            <Field label="description" value={graph.metadata.description} multiline />
          )}
          {graph.metadata.authoringNotes && (
            <Field label="notes" value={graph.metadata.authoringNotes} multiline />
          )}
          <div style={{ marginTop: 12 }}>
            <a
              href={`/archetypes/${graph.id}.json`}
              target="_blank"
              rel="noreferrer"
              style={{ color: palette.acornOrange }}
            >
              open JSON →
            </a>
          </div>
        </>
      )}
    </aside>
  );
}

function Field({ label, value, multiline }: { label: string; value: string; multiline?: boolean }) {
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ opacity: 0.5, fontSize: 10, letterSpacing: 1, textTransform: 'uppercase' }}>
        {label}
      </div>
      <div style={{ wordBreak: multiline ? 'break-word' : 'break-all', whiteSpace: multiline ? 'pre-wrap' : 'normal' }}>
        {value}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function boundingBox(graph: SegmentGraph): Box3 {
  const box = new Box3();
  for (const s of graph.segments) {
    box.expandByPoint(new Vector3(...s.axis.p0));
    box.expandByPoint(new Vector3(...s.axis.p3));
  }
  if (box.isEmpty()) box.expandByPoint(new Vector3(0, 0, 0));
  return box;
}
